import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isToday } from "date-fns";
import { FaBookOpen } from "react-icons/fa";
import { BookOpen, CalendarDays, CircleCheck, Flag, List, PencilLine, User, Users } from "lucide-react";
import Button from "../../components/Button/Button";
import CardHeader from "../../components/Card/CardHeader";
import Loader from "../../components/Loader/Loader";
import DataNotFound from "../../utils/DataNotFound";
import StatCard from "../../components/StatCard/StatCard";
import WeekNavigator from "../../components/WeekNavigator/WeekNavigator";
import SegmentedControl from "../../components/SegmentedControl/SegmentedControl";
import LessonCard from "./components/LessonCard";
import EmptySlotCard from "./components/EmptySlotCard";
import LessonCalendarView from "./components/LessonCalendarView";
import FlagLessonModal from "./components/FlagLessonModal";
import MarkCompleteModal from "./components/MarkCompleteModal";
import WeeklyReviewModal from "./components/WeeklyReviewModal";
import AllLessonPlansView from "./AllLessonPlansView";
import { useLessonPlans } from "./hooks/useLessonPlans";
import { LESSON_PERMISSIONS, LESSON_STATUS } from "./LessonPlannerConstant";
import { formatDate, getWeekRange, groupLessonsByDay, hasPermission, toApiDate } from "./utils/lessonPlanUtils";
import "./LessonPlanner.css";

const VIEW_OPTIONS = [
  { value: "list", label: "List", icon: List },
  { value: "calendar", label: "Calendar", icon: CalendarDays },
];

const SCOPE = { ALL: "all", MINE: "mine" };

const SCOPE_OPTIONS = [
  { value: SCOPE.ALL, label: "All Teachers", icon: Users },
  { value: SCOPE.MINE, label: "My Planner", icon: User },
];

const STATUS_OPTIONS = [
  { value: LESSON_STATUS.PENDING, label: "Pending" },
  { value: LESSON_STATUS.COMPLETED, label: "Completed" },
];

const LessonPlannerList = () => {
  const navigate = useNavigate();
  const canViewAll = useMemo(() => hasPermission(LESSON_PERMISSIONS.VIEW_ALL), []);
  const [scope, setScope] = useState(canViewAll ? SCOPE.ALL : SCOPE.MINE);
  const isMine = scope === SCOPE.MINE;
  const [weekStart, setWeekStart] = useState(() => getWeekRange(new Date()).start);
  const [view, setView] = useState("list");
  const [statusTab, setStatusTab] = useState(LESSON_STATUS.PENDING);
  const [flagTarget, setFlagTarget] = useState(null);
  const [completeTarget, setCompleteTarget] = useState(null);
  const [isWeeklyOpen, setIsWeeklyOpen] = useState(false);

  const { lessons, summary, isLoading, actionId, fetchLessons, markComplete, flagLesson, removeLesson } =
    useLessonPlans();

  useEffect(() => {
    if (isMine) fetchLessons(weekStart);
  }, [isMine, weekStart, fetchLessons]);

  // Pending tab also shows timetable slots that still need a plan
  const groupedDays = useMemo(() => {
    const visible = lessons.filter((lesson) =>
      statusTab === LESSON_STATUS.COMPLETED
        ? lesson.hasPlan && lesson.status === LESSON_STATUS.COMPLETED
        : !lesson.hasPlan || lesson.status === LESSON_STATUS.PENDING,
    );
    return groupLessonsByDay(visible, weekStart);
  }, [lessons, statusTab, weekStart]);

  /* ================= HANDLERS ================= */
  const viewLesson = (lesson) => navigate(`/lesson-planner/view/${lesson.id}`);
  const editLesson = (lesson) => navigate(`/lesson-planner/update/${lesson.id}`);

  const createLesson = (slot) => {
    const params = new URLSearchParams();
    if (slot?.programId) params.set("program_id", slot.programId);
    if (slot?.subjectId) params.set("subject_id", slot.subjectId);
    if (slot?.lessonDate) params.set("lesson_date", toApiDate(slot.lessonDate));
    const query = params.toString();
    navigate(`/lesson-planner/create${query ? `?${query}` : ""}`);
  };

  const deleteLesson = (lesson) => {
    if (window.confirm("Are you sure you wish to delete this item?")) {
      removeLesson(lesson.id);
    }
  };

  const submitComplete = async (remark) => {
    const isSaved = await markComplete(completeTarget.id, remark);
    if (isSaved) setCompleteTarget(null);
  };

  const submitFlag = async (flag) => {
    const isSaved = await flagLesson(flagTarget.id, flag);
    if (isSaved) setFlagTarget(null);
  };

  /* ================= RENDER ================= */
  const renderList = () => {
    if (groupedDays.length === 0) return <DataNotFound />;

    return groupedDays.map(({ date, lessons: dayLessons }) => (
      <section key={date.toISOString()} className="lp-day">
        <header className="lp-day__header">
          <h3>{formatDate(date, "EEEE, d MMM")}</h3>
          <span className="text-muted small">
            {dayLessons.length} {dayLessons.length === 1 ? "lesson" : "lessons"}
          </span>
          {isToday(date) && <span className="lp-chip lp-tone-primary">Today</span>}
          <span className="lp-day__rule" />
        </header>
        <div className="lp-day__lessons">
          {dayLessons.map((lesson, index) =>
            lesson.hasPlan ? (
              <LessonCard
                key={lesson.id}
                lesson={lesson}
                isBusy={actionId === lesson.id}
                onComplete={setCompleteTarget}
                onFlag={setFlagTarget}
                onView={viewLesson}
                onEdit={editLesson}
                onDelete={deleteLesson}
              />
            ) : (
              <EmptySlotCard key={`slot-${index}`} lesson={lesson} onCreate={createLesson} />
            ),
          )}
        </div>
      </section>
    ));
  };

  return (
    <div className="card lp-page">
      <CardHeader
        title="Lesson Planner"
        icon={<FaBookOpen />}
        button={
          <div className="d-flex gap-2">
            <Button variant="outline-primary" onClick={() => createLesson()}>
              Create Lesson Plan
            </Button>
            {isMine && <Button onClick={() => setIsWeeklyOpen(true)}>Weekly Update</Button>}
          </div>
        }
      />

      <div className="card-body">
        {canViewAll && (
          <div className="mb-3">
            <SegmentedControl options={SCOPE_OPTIONS} value={scope} onChange={setScope} />
          </div>
        )}

        {isMine ? (
          <>
            <p className="text-muted mb-3">Plan, organize, and track lessons for your classes — all in one place.</p>

            <div className="row">
              <StatCard value={summary.total} label="Total lesson plans" icon={BookOpen} tone="primary" />
              <StatCard value={summary.completed} label="Completed" icon={CircleCheck} tone="success" />
              <StatCard value={summary.pending} label="Pending" icon={PencilLine} tone="warning" />
              <StatCard value={summary.flagged} label="Flagged" icon={Flag} tone="danger" />
            </div>

            <div className="lp-toolbar">
              <WeekNavigator weekStart={weekStart} onChange={setWeekStart} />
              <SegmentedControl options={VIEW_OPTIONS} value={view} onChange={setView} />
            </div>

            {isLoading ? (
              <Loader />
            ) : view === "calendar" ? (
              <LessonCalendarView weekStart={weekStart} lessons={lessons} onView={viewLesson} onCreate={createLesson} />
            ) : (
              <>
                <div className="lp-status-tabs">
                  <SegmentedControl options={STATUS_OPTIONS} value={statusTab} onChange={setStatusTab} variant="pill" />
                </div>
                {renderList()}
              </>
            )}
          </>
        ) : (
          <AllLessonPlansView />
        )}
      </div>

      <MarkCompleteModal
        lesson={completeTarget}
        isSubmitting={Boolean(completeTarget) && actionId === completeTarget.id}
        onClose={() => setCompleteTarget(null)}
        onSubmit={submitComplete}
      />

      <FlagLessonModal
        lesson={flagTarget}
        isSubmitting={Boolean(flagTarget) && actionId === flagTarget.id}
        onClose={() => setFlagTarget(null)}
        onSubmit={submitFlag}
      />

      <WeeklyReviewModal
        isOpen={isWeeklyOpen}
        weekStart={weekStart}
        onClose={() => setIsWeeklyOpen(false)}
        onSubmitted={() => fetchLessons(weekStart)}
      />
    </div>
  );
};

export default LessonPlannerList;
